package com.eladfinish.bible.contest.screens.lexicographicalSort.ui

import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MenuAnchorType
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.utils.toOtiot
import kotlinx.collections.immutable.ImmutableList

/**
 * Simple, self-contained book + chapter picker, built from
 * [com.eladfinish.bible.contest.domain.BooksRepository] data, used by the
 * lexicographical sort screen. Unlike [com.eladfinish.bible.contest.screens.browse.tableOfContents.ui.BrowseTabScreen]'s
 * `BooksAndChaptersContainer`, this is not coupled to `BrowseTabUiState`.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LexicographicalSortBookChapterPicker(
    bookNames: ImmutableList<String>,
    selectedBookIndex: Int,
    selectedChapterIndex: Int,
    numberOfChaptersInSelectedBook: Int,
    onBookSelected: (Int) -> Unit,
    onChapterSelected: (Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(modifier = modifier.fillMaxWidth()) {
        var isBookMenuExpanded by rememberSaveable { mutableStateOf(false) }
        var isChapterMenuExpanded by rememberSaveable { mutableStateOf(false) }

        ExposedDropdownMenuBox(
            modifier = Modifier.weight(2f),
            expanded = isBookMenuExpanded,
            onExpandedChange = { isBookMenuExpanded = it },
        ) {
            OutlinedTextField(
                modifier = Modifier
                    .menuAnchor(type = MenuAnchorType.PrimaryNotEditable, enabled = true)
                    .fillMaxWidth()
                    .wrapContentHeight(),
                readOnly = true,
                value = bookNames.getOrNull(selectedBookIndex - 1).orEmpty(),
                onValueChange = {},
                label = { Text(text = "ספר") },
                trailingIcon = {
                    ExposedDropdownMenuDefaults.TrailingIcon(expanded = isBookMenuExpanded)
                },
            )

            ExposedDropdownMenu(
                expanded = isBookMenuExpanded,
                onDismissRequest = { isBookMenuExpanded = false },
            ) {
                bookNames.forEachIndexed { index, bookName ->
                    DropdownMenuItem(
                        text = { Text(text = bookName) },
                        onClick = {
                            onBookSelected(index + 1)
                            isBookMenuExpanded = false
                        },
                    )
                }
            }
        }

        Spacer(modifier = Modifier.width(8.dp))

        ExposedDropdownMenuBox(
            modifier = Modifier.weight(1f),
            expanded = isChapterMenuExpanded,
            onExpandedChange = { isChapterMenuExpanded = it },
        ) {
            OutlinedTextField(
                modifier = Modifier
                    .menuAnchor(type = MenuAnchorType.PrimaryNotEditable, enabled = true)
                    .fillMaxWidth()
                    .wrapContentHeight(),
                readOnly = true,
                value = selectedChapterIndex.toOtiot(),
                onValueChange = {},
                label = { Text(text = "פרק") },
                trailingIcon = {
                    ExposedDropdownMenuDefaults.TrailingIcon(expanded = isChapterMenuExpanded)
                },
            )

            ExposedDropdownMenu(
                expanded = isChapterMenuExpanded,
                onDismissRequest = { isChapterMenuExpanded = false },
            ) {
                (1..numberOfChaptersInSelectedBook).forEach { chapterIndex ->
                    DropdownMenuItem(
                        text = { Text(text = chapterIndex.toOtiot()) },
                        onClick = {
                            onChapterSelected(chapterIndex)
                            isChapterMenuExpanded = false
                        },
                    )
                }
            }
        }
    }
}
